<template>
  <ul
    v-if="numberOfPages > 1"
    class="flex w-full flex-row items-baseline justify-center gap-2"
  >
    <li class="text-lg">
      <static-search-link
        class="text-blue-30 hover:text-blue-50"
        :to="previousLink"
        v-if="previousLink"
        >&lt; Previous
      </static-search-link>
      <template v-else> &lt; Previous</template>
    </li>

    <li v-for="link in firstLinks" :key="link.page">
      <static-search-link :to="link" v-if="link.page !== currentPage">
        <SearchPaginationButton clickable>
          {{ link.page }}
        </SearchPaginationButton>
      </static-search-link>
      <SearchPaginationButton v-else active>
        {{ link.page }}
      </SearchPaginationButton>
    </li>

    <li v-if="showFirstBreak">
      <SearchPaginationButton>...</SearchPaginationButton>
    </li>

    <li v-for="link in sliderLinks" :key="link.page">
      <static-search-link v-if="link.page !== currentPage" :to="link">
        <SearchPaginationButton clickable>
          {{ link.page }}
        </SearchPaginationButton>
      </static-search-link>
      <SearchPaginationButton v-else active>
        {{ link.page }}
      </SearchPaginationButton>
    </li>

    <li v-if="showLastBreak">
      <SearchPaginationButton>...</SearchPaginationButton>
    </li>

    <li v-for="link in lastLinks" :key="link.page">
      <static-search-link v-if="link.page !== currentPage" :to="link">
        <SearchPaginationButton clickable>
          {{ link.page }}
        </SearchPaginationButton>
      </static-search-link>
      <SearchPaginationButton v-else active>
        {{ link.page }}
      </SearchPaginationButton>
    </li>

    <li class="text-lg">
      <static-search-link
        class="text-blue-30 hover:text-blue-50"
        :to="nextLink"
        v-if="nextLink"
        >Next &gt;
      </static-search-link>
      <span v-else>Next &gt;</span>
    </li>
  </ul>
</template>

<script>
import { mapState } from 'vuex';
import SearchPaginationButton from 'Components/SearchPaginationButton.vue';
import StaticSearchLink from './StaticSearchLink.vue';
import { isNull, isEmpty, omitBy, range } from 'lodash';

/**
 * Pagination for search results
 *
 * Region Page ~{page}.htm
 * County Page ~{page}.htm
 * Town Page ~{page}.htm
 *
 *
 * Industry County Page ?page={page}
 * Search Page ?page={page}
 * All pages can use ?page={page}, Removing ~{page} from URL in links
 * Support ~{page} url directly loaded by SSR
 *
 * Under 10 pages, show all pages
 * Current Page under 8, show first 8 pages, ..., last 2 pages
 * Current Page over 8 less than last 8, show first 2 pages ... 3 pages before current, current, 3 pages after, ..., last 2 pages
 * Current Page in last 8, show first 2 pages, ..., last 8 pages
 *
 * First Page List -- First 2, First 8
 * First Break - [...]
 * Second Page List -- Middle 7
 * Second Break - [...]
 * Third Page List -- Last 2, Last 8
 *
 */
export default {
  name: 'SearchPagination',
  components: { SearchPaginationButton, StaticSearchLink },
  props: {
    static: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    /**
     * Generate the correct object for <static-search-link :to="" /> 
     * 
     * If we don't return an object, query strings seem to get lost in SSR
     * 
     * @param number page
     * @return object 
     */
    getPageUrl(page) {
      // RouterLink/StaticSearchLink will ignore any keys it doesn't want to know about
      let retData = { path: null, query: null, page: page };

      const newQueryParameters = {
        ...omitBy(this.$route.query, isEmpty), // Current Parameters
      };
      const pageParameters = new URLSearchParams(newQueryParameters);

      // Remove legacy ~page.htm (e.g ~3.htm) pagination (Still supported by back end for SEO)
      let url = this.$route.path.replace(/~\d+/, '');
      //console.log(`Url: ${url}, Page: ${page}`);

      // yuck! for some reason, some pages still use the ~XX page number
      if (['town', 'county', 'region'].includes(this.pageType())) {
        url = url.replace(/\.htm/, '~'+page+'.htm');
      } else {
        pageParameters.set('page', page);
      }

      if (page == 1) {
        url = url.replace(/~1\.htm/, '.htm');
        pageParameters.delete('page');
      }

      retData.path = url;
      retData.query = Object.fromEntries(pageParameters);
      return retData;
    },
    // 2024-09-25 [JB]: Deprecated function now I think.
    toPageObject(page) {
      let  pageData = this.getPageUrl(page);
      return {
        page,
        path: pageData.path,
        query: pageData.query
      };
    },
    pageType() {
        return this.parameters.page_type;
    },
  },
  computed: {
    ...mapState('search', ['meta', 'currentPageParameters', 'parameters']),
    numberOfPages() {
      return (this.currentPageParameters.limit == 0)
        ? 0
        : Math.ceil(this.meta.count / this.currentPageParameters.limit);
    },
    currentPage() {
      const currentPage = Number(this.currentPageParameters?.page);
      return isNaN(currentPage) ? 1 : currentPage;
    },
    nextLink() {
      const nextPage = this.currentPage + 1;
      return nextPage <= this.numberOfPages ? this.getPageUrl(nextPage) : null;
    },
    previousLink() {
      const previousPage = this.currentPage - 1;
      return previousPage > 0 ? this.getPageUrl(previousPage) : null;
    },
    firstLinks() {
      const startPage = 1;
      let endPage = 2;

      if (this.numberOfPages < 10) {
        endPage = this.numberOfPages;
      } else if (this.currentPage < 8) {
        endPage = 8;
      }

      // Range is not inclusive, so we add 1 to the endPage
      return range(startPage, endPage + 1).map(this.getPageUrl);
    },
    sliderLinks() {
      // Under 10 pages are all displayed in the first links
      // If the current page is less than 8 there we display no sliding links section
      // If the page is in the last 8 pages we don't display a sliding links section
      if (
        this.numberOfPages <= 10 ||
        this.currentPage < 8 ||
        this.numberOfPages - this.currentPage < 8
      ) {
        return [];
      }

      const startPage = this.currentPage - 3;
      const endPage = this.currentPage + 3;
      return range(startPage, endPage + 1).map(this.getPageUrl);
    },
    lastLinks() {
      // Under 10 pages are all displayed in the first links
      if (this.numberOfPages <= 10) {
        return [];
      }
      // Show last 2 pages
      let startPage = this.numberOfPages - 1;
      let endPage = this.numberOfPages;

      // If there are less than 8 pages remaining, display the last 8 pages
      if (this.numberOfPages - this.currentPage < 8) {
        startPage = this.numberOfPages - 8;
      }

      return range(startPage, endPage + 1).map(this.getPageUrl);
    },
    showFirstBreak() {
      // Break between the first and last links, or first and slider links
      return this.firstLinks.length !== 0 && this.lastLinks.length !== 0;
    },
    showLastBreak() {
      // Break between the slider and last links
      return this.sliderLinks.length !== 0 && this.lastLinks.length !== 0;
    },
  },
};
</script>
